import React from "react";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, errorInfo);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-lg w-full glass-card p-8">
          <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The page failed to render. Try reloading—if it persists, we’ll inspect the crash details.
          </p>
          {this.state.error?.message ? (
            <pre className="mt-4 whitespace-pre-wrap text-xs text-muted-foreground/80 bg-background-secondary/40 rounded-md p-3 overflow-auto">
              {this.state.error.message}
            </pre>
          ) : null}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-foreground/90 hover:bg-secondary"
              onClick={() => this.setState({ hasError: false, error: undefined })}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
